import {useCallback} from 'react';
import {useForm} from 'react-hook-form';
import {useNavigate} from 'react-router-dom';

import {DateFormatEnum, QueryParamsEnum, RefundEnum} from 'presentation/constants';
import {SelectDatesType} from 'presentation/hooks/useModalDatePicker';
import {useQueryParams} from 'presentation/pages';
import {parseDate, stringfyDate} from 'presentation/utils';
import {
	RefundByDateSchema,
	RefundByProtocolSchema,
	refundByDateSchema,
	refundByProtocolSchema,
} from '../pages/ConsultRefund/components/InitialStep/schema';
import {consultRefundEnum} from '../constants/consultRefundEnum';

type SearchByDateParamsType = RefundByDateSchema & {
	isPreview?: boolean;
};

export function useConsultRefund() {
	const navigate = useNavigate();
	const {setSearchParams, SEARCH_PARAMS} = useQueryParams();

	const {step, consultInitialDate, consultEndDate, consultProtocolNumber} = SEARCH_PARAMS;

	const methods = useForm({
		defaultValues: {
			protocolNumber: '',
			rangeDate: '',
		},
	});

	const {
		watch,
		formState: {isSubmitting},
	} = methods;

	const rangeDateWatched = watch('rangeDate');
	const protocolNumberWatched = watch('protocolNumber');
	const IS_EMPTY_RANGE_DATE = rangeDateWatched === ' - ';

	const isDisabled =
		(!watch('protocolNumber') && !rangeDateWatched) || IS_EMPTY_RANGE_DATE || isSubmitting;

	const handleResetQuery = useCallback(() => {
		setSearchParams(
			prev => {
				prev.delete(QueryParamsEnum.CONSULT_PROTOCOL_NUMBER);
				prev.delete(QueryParamsEnum.CONSULT_END_DATE);
				prev.delete(QueryParamsEnum.CONSULT_INITIAL_DATE);
				prev.delete(QueryParamsEnum.STEP);
				return prev;
			},
			{
				replace: true,
			},
		);
	}, []);

	const handleBack = useCallback(() => {
		if (!step) {
			navigate(RefundEnum.REFUND);
			return;
		}
		handleResetQuery();
	}, [step]);

	const handleSearchByProtocol = useCallback(({protocolNumber}: RefundByProtocolSchema) => {
		const {error} = refundByProtocolSchema.safeParse({protocolNumber});

		if (error) {
			const onlyNumbersError = error.errors.find(
				validationError => validationError.path[0] === consultRefundEnum.PROTOCOLNUMBER,
			)?.message;

			methods.setError('protocolNumber', {
				message: onlyNumbersError,
			});
			return;
		}

		setSearchParams(
			prev => {
				prev.set(QueryParamsEnum.CONSULT_PROTOCOL_NUMBER, protocolNumber);
				prev.set(QueryParamsEnum.STEP, '2');
				return prev;
			},
			{
				replace: true,
			},
		);
	}, []);

	const handleSearchByDate = useCallback(({rangeDate}: SearchByDateParamsType) => {
		const {error} = refundByDateSchema.safeParse({rangeDate});

		if (error) {
			const {fieldErrors} = error.flatten();
			Object.entries(fieldErrors).forEach(([field, issues]) => {
				if (issues) {
					methods.setError(field as keyof RefundByDateSchema, {
						type: 'manual',
						message: issues.join('. '),
					});
				}
			});
			return;
		}

		const [initialDate, endDate] = rangeDate.split(' - ');
		const formattedInitialDate = stringfyDate(
			parseDate(initialDate) || null,
			DateFormatEnum.DATE_FORMAT_4,
		);
		const formattedEndDate = stringfyDate(
			parseDate(endDate) || null,
			DateFormatEnum.DATE_FORMAT_4,
		);

		setSearchParams(
			prev => {
				prev.set(QueryParamsEnum.CONSULT_INITIAL_DATE, formattedInitialDate);
				prev.set(QueryParamsEnum.CONSULT_END_DATE, formattedEndDate);
				prev.set(QueryParamsEnum.STEP, '2');

				return prev;
			},
			{
				replace: true,
			},
		);
	}, []);

	const handleClearInput = useCallback(() => {
		setSearchParams(
			prev => {
				prev.delete(QueryParamsEnum.CONSULT_PROTOCOL_NUMBER);
				return prev;
			},
			{
				replace: true,
			},
		);
	}, []);

	const handleExitFlow = useCallback(() => {
		navigate(RefundEnum.REFUND);
	}, []);

	const handleSetDates = useCallback(
		(dates: SelectDatesType) => {
			const firstDate = stringfyDate(dates.firstDate, DateFormatEnum.DATE_FORMAT_8);
			const secondDate = stringfyDate(dates.secondDate, DateFormatEnum.DATE_FORMAT_8);
			methods.setValue('rangeDate', `${firstDate} - ${secondDate}`);
			methods.trigger('rangeDate');
		},
		[consultProtocolNumber],
	);

	const searchByProtocol = !!watch('protocolNumber');

	const shouldShowAlertError =
		(!rangeDateWatched || IS_EMPTY_RANGE_DATE) && !protocolNumberWatched;

	return {
		handleSetDates,
		handleBack,
		handleClearInput,
		handleResetQuery,
		step,
		handleExitFlow,
		consultEndDate,
		consultInitialDate,
		consultProtocolNumber,
		handleSearchByProtocol,
		handleSearchByDate,
		methods,
		isDisabled,
		searchByProtocol,
		shouldShowAlertError,
	};
}
